import _ from 'underscore';

import preferences from 'Preferences';
import {BenchlingAction} from 'redux-stores/Action';

import {ModifiedNucleotidesPreferences} from './MonomerConstants';
import {NucleotidesUIActionType} from './NucleotidesUIReduxActions';

export interface AllNucleotidesUIState {
  [tabId: string]: NucleotidesUIState;
}

interface NucleotidesUIState {
  hoveredMonomerIds: Array<string | null>;
  hoveredIdx: number | null;
  editingNucleotideIndices: Array<number>;
  selectedPositions: Set<number>;
  tablePaginationOffset: number;
}

const ALL_NUCLEOTIDES_UI_INITIAL_STATE = {};

const NUCLEOTIDES_UI_INITIAL_STATE = {
  hoveredMonomerIds: [],
  hoveredIdx: null,
  editingNucleotideIndices: [],
  selectedPositions: new Set<number>(),
  tablePaginationOffset: 0,
};

function reduceNucleotidesUIByTab(
  state: NucleotidesUIState = NUCLEOTIDES_UI_INITIAL_STATE,
  action: BenchlingAction
): NucleotidesUIState {
  switch (action.type) {
    case NucleotidesUIActionType.HOVER_MONOMERS: {
      return {...state, hoveredMonomerIds: action.monomerIds};
    }
    case NucleotidesUIActionType.HOVER_NUCLEOTIDE_IDX: {
      return {...state, hoveredIdx: action.nucleotideIdx};
    }
    case NucleotidesUIActionType.EDIT_NUCLEOTIDE_INDICES: {
      return {
        ...state,
        editingNucleotideIndices: [
          ...state.editingNucleotideIndices.filter((idx) => !action.nucleotideIndices.includes(idx)),
          ...action.nucleotideIndices,
        ],
      };
    }
    case NucleotidesUIActionType.FINISH_EDITING_NUCLEOTIDE_INDEX: {
      return {
        ...state,
        editingNucleotideIndices: state.editingNucleotideIndices.filter(
          (idx) => idx !== action.nucleotideIdx
        ),
      };
    }
    case NucleotidesUIActionType.SELECT_NUCLEOTIDE_ROWS: {
      const newPositions = new Set([...state.selectedPositions]);
      action.positions.forEach((p) => newPositions.add(p));
      return {...state, selectedPositions: newPositions};
    }
    case NucleotidesUIActionType.DESELECT_NUCLEOTIDE_ROWS: {
      const newPositions = new Set([...state.selectedPositions]);
      action.positions.forEach((p) => newPositions.delete(p));
      return {...state, selectedPositions: newPositions};
    }
    case NucleotidesUIActionType.CLEAR_NUCLEOTIDE_ROW_SELECTION: {
      return {...state, selectedPositions: new Set()};
    }
    case NucleotidesUIActionType.UPDATE_TABLE_PAGINATION_OFFSET: {
      return {...state, tablePaginationOffset: action.offset};
    }
    case NucleotidesUIActionType.PAGE_NUCLEOTIDE_ROW_INTO_VIEW: {
      const pageSize = preferences.get(ModifiedNucleotidesPreferences.NUCLEOTIDES_TABLE_PAGE_SIZE);
      const newOffset = action.position - (action.position % pageSize);
      return {...state, tablePaginationOffset: newOffset};
    }
    default:
      return state;
  }
}

export default function reduceNucleotidesUI(
  state: AllNucleotidesUIState = ALL_NUCLEOTIDES_UI_INITIAL_STATE,
  action: BenchlingAction
): AllNucleotidesUIState {
  switch (action.type) {
    case NucleotidesUIActionType.OPEN_SEQUENCE:
      return {...state, [action.sequenceId]: NUCLEOTIDES_UI_INITIAL_STATE};
    case NucleotidesUIActionType.CLOSE_SEQUENCE:
      // eslint-disable-next-line no-restricted-syntax
      return _.omit(state, [action.sequenceId]);
    case NucleotidesUIActionType.HOVER_MONOMERS:
    case NucleotidesUIActionType.HOVER_NUCLEOTIDE_IDX:
    case NucleotidesUIActionType.EDIT_NUCLEOTIDE_INDICES:
    case NucleotidesUIActionType.FINISH_EDITING_NUCLEOTIDE_INDEX:
    case NucleotidesUIActionType.SELECT_NUCLEOTIDE_ROWS:
    case NucleotidesUIActionType.DESELECT_NUCLEOTIDE_ROWS:
    case NucleotidesUIActionType.CLEAR_NUCLEOTIDE_ROW_SELECTION:
    case NucleotidesUIActionType.UPDATE_TABLE_PAGINATION_OFFSET:
    case NucleotidesUIActionType.PAGE_NUCLEOTIDE_ROW_INTO_VIEW:
      return {
        ...state,
        [action.sequenceId]: reduceNucleotidesUIByTab(state[action.sequenceId], action),
      };
    default:
      return state;
  }
}
